import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { 
  ChevronRight, User, Briefcase, ArrowRight, 
  Eye, EyeOff, AlertCircle
} from 'lucide-react';

export default function EncheresConnexion() {
  const { API, login, user } = useContext(AppContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get('redirect');
  
  const [mode, setMode] = useState(null); // 'acheteur' or 'vendeur'
  const [isLogin, setIsLogin] = useState(true); // Login vs Register (for acheteur only)
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    entreprise: ''
  });

  // Redirect if already logged in with correct role
  useEffect(() => {
    if (user) {
      // If there's a redirect URL, use it
      if (redirectUrl) {
        navigate(redirectUrl);
        return;
      }
      // Otherwise, redirect to appropriate dashboard
      if (user.roles?.includes('enchere_vendeur')) {
        navigate('/encheres/espace-vendeur');
      } else if (user.roles?.includes('enchere_acheteur')) {
        navigate('/encheres/mon-compte');
      }
    }
  }, [user, navigate, redirectUrl]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (mode === 'acheteur') {
        if (isLogin) {
          // Login as buyer
          const response = await axios.post(`${API}/auth/login`, {
            email: formData.email,
            password: formData.password
          });
          
          login(response.data.user, response.data.token);
          toast.success('Connexion réussie');
          navigate(redirectUrl || '/encheres/mon-compte');
        } else {
          // Register as buyer
          const response = await axios.post(`${API}/auth/register`, {
            email: formData.email,
            password: formData.password,
            name: formData.name,
            company: formData.entreprise,
            role: 'enchere_acheteur'
          });
          
          login(response.data.user, response.data.token);
          toast.success('Compte créé avec succès');
          navigate(redirectUrl || '/encheres/mon-compte');
        }
      } else if (mode === 'vendeur') {
        // Login only for sellers (no registration)
        const response = await axios.post(`${API}/auth/login`, {
          email: formData.email,
          password: formData.password
        });
        
        const userData = response.data.user;
        
        // Verify seller role
        if (!userData.roles?.includes('enchere_vendeur')) {
          toast.error('Ce compte n\'a pas accès à l\'espace vendeur. Contactez-nous pour demander un accès.');
          return;
        }
        
        // Check if password change required (first login)
        if (userData.password_change_required) {
          login(userData, response.data.token);
          navigate('/encheres/changer-mot-de-passe');
          return;
        }
        
        login(userData, response.data.token);
        toast.success('Connexion réussie');
        navigate('/encheres/espace-vendeur');
      }
    } catch (error) {
      let msg = 'Erreur de connexion';
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(detail)) {
        msg = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      } else if (detail && typeof detail === 'object') {
        msg = detail.msg || detail.message || JSON.stringify(detail);
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // Profile selection screen
  if (!mode) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Helmet>
          <title>Connexion Enchères | GEOTS</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>

        <div className="bg-[#0A2540] text-white">
          <div className="container mx-auto px-4 py-12">
            <nav className="flex items-center gap-2 text-sm text-gray-300 mb-6">
              <Link to="/" className="hover:text-white">Accueil</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/encheres" className="hover:text-white">Enchères</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-white font-medium">Connexion</span>
            </nav>

            <h1 className="text-2xl md:text-3xl font-bold">
              Accès aux enchères
            </h1>
            <p className="text-gray-300 mt-2">
              Choisissez votre profil pour continuer
            </p>
          </div>
        </div>

        <div className="container mx-auto px-4 py-12">
          <div className="max-w-2xl mx-auto grid md:grid-cols-2 gap-6">
            {/* Buyer Option */}
            <Card 
              className="cursor-pointer hover:shadow-lg transition-all hover:border-[#F0A22E] group"
              onClick={() => setMode('acheteur')}
            >
              <CardContent className="p-8 text-center">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-200 transition-colors">
                  <User className="w-8 h-8 text-blue-600" />
                </div>
                <h2 className="text-xl font-bold text-[#0A2540] mb-2">
                  Je suis acheteur
                </h2>
                <p className="text-gray-600 mb-4">
                  Créez un compte ou connectez-vous pour participer aux ventes
                </p>
                <Button className="w-full group-hover:bg-[#F0A22E] group-hover:text-white transition-colors">
                  Continuer
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>

            {/* Seller Option */}
            <Card 
              className="cursor-pointer hover:shadow-lg transition-all hover:border-[#F0A22E] group"
              onClick={() => setMode('vendeur')}
            >
              <CardContent className="p-8 text-center">
                <div className="bg-[#F0A22E]/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-[#F0A22E]/30 transition-colors">
                  <Briefcase className="w-8 h-8 text-[#F0A22E]" />
                </div>
                <h2 className="text-xl font-bold text-[#0A2540] mb-2">
                  Je suis vendeur
                </h2>
                <p className="text-gray-600 mb-4">
                  Connectez-vous à votre espace vendeur
                </p>
                <Button variant="outline" className="w-full group-hover:border-[#F0A22E] group-hover:text-[#F0A22E] transition-colors">
                  Se connecter
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Seller access note */}
          <div className="max-w-2xl mx-auto mt-8">
            <Card className="bg-gray-50 border-dashed">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <AlertCircle className="w-6 h-6 text-[#F0A22E] flex-shrink-0" />
                  <div>
                    <p className="font-medium text-[#0A2540]">
                      L&apos;accès vendeur est délivré par GEOTS
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      Après étude de votre demande, nous vous transmettrons vos identifiants de connexion.
                    </p>
                    <Link to="/encheres/devenir-vendeur" className="inline-flex items-center gap-2 text-[#F0A22E] hover:underline text-sm mt-2">
                      Demander un accès vendeur
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Login/Register form
  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>{mode === 'acheteur' ? 'Espace Acheteur' : 'Espace Vendeur'} | GEOTS Enchères</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="bg-[#0A2540] text-white">
        <div className="container mx-auto px-4 py-12">
          <nav className="flex items-center gap-2 text-sm text-gray-300 mb-6">
            <Link to="/" className="hover:text-white">Accueil</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/encheres" className="hover:text-white">Enchères</Link>
            <ChevronRight className="w-4 h-4" />
            <button onClick={() => setMode(null)} className="hover:text-white">Connexion</button>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white font-medium">
              {mode === 'acheteur' ? 'Espace Acheteur' : 'Espace Vendeur'}
            </span>
          </nav>

          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-lg ${mode === 'acheteur' ? 'bg-blue-500/20' : 'bg-[#F0A22E]/20'}`}>
              {mode === 'acheteur' ? (
                <User className="w-8 h-8 text-blue-400" />
              ) : (
                <Briefcase className="w-8 h-8 text-[#F0A22E]" />
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold">
                {mode === 'acheteur' ? 'Espace Acheteur' : 'Espace Vendeur'}
              </h1>
              <p className="text-gray-300">
                {mode === 'acheteur' 
                  ? 'Connectez-vous ou créez un compte pour enchérir'
                  : 'Connectez-vous à votre espace vendeur'
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-md mx-auto">
          <Card>
            <CardHeader>
              <CardTitle>
                {mode === 'acheteur' 
                  ? (isLogin ? 'Connexion' : 'Créer un compte') 
                  : 'Connexion vendeur'
                }
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {mode === 'acheteur' && !isLogin && (
                  <>
                    <div>
                      <Label htmlFor="name">Nom complet</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="entreprise">Entreprise</Label>
                      <Input
                        id="entreprise"
                        value={formData.entreprise}
                        onChange={(e) => handleChange('entreprise', e.target.value)}
                      />
                    </div>
                  </>
                )}

                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="password">Mot de passe</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => handleChange('password', e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className={`w-full ${mode === 'vendeur' ? 'bg-[#F0A22E] hover:bg-[#d89426]' : ''}`}
                  disabled={loading}
                >
                  {loading ? (
                    <span className="animate-pulse">Chargement...</span>
                  ) : (
                    mode === 'acheteur' 
                      ? (isLogin ? 'Se connecter' : 'Créer mon compte')
                      : 'Se connecter'
                  )}
                </Button>

                {mode === 'acheteur' && (
                  <div className="text-center text-sm">
                    {isLogin ? (
                      <p className="text-gray-600">
                        Pas encore de compte ?{' '}
                        <button 
                          type="button"
                          onClick={() => setIsLogin(false)}
                          className="text-[#F0A22E] hover:underline font-medium"
                        >
                          Créer un compte
                        </button>
                      </p>
                    ) : (
                      <p className="text-gray-600">
                        Déjà un compte ?{' '}
                        <button 
                          type="button"
                          onClick={() => setIsLogin(true)}
                          className="text-[#F0A22E] hover:underline font-medium"
                        >
                          Se connecter
                        </button>
                      </p>
                    )}
                  </div>
                )}

                {mode === 'vendeur' && (
                  <div className="bg-gray-50 p-4 rounded-lg text-center">
                    <p className="text-sm text-gray-600">
                      Vous n&apos;avez pas encore d&apos;accès vendeur ?
                    </p>
                    <Link 
                      to="/encheres/devenir-vendeur" 
                      className="text-sm text-[#F0A22E] hover:underline font-medium"
                    >
                      Demander un accès vendeur →
                    </Link>
                  </div>
                )}
              </form>
            </CardContent>
          </Card>

          <div className="text-center mt-6">
            <button 
              onClick={() => setMode(null)}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              ← Changer de profil
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
